import React, { useEffect } from "react";
import styles from "./styles.module.scss";
import AccountWrap from "..";
import PagePanel from "app/components/PagePanel";
import BillingInfoView from "app/components/subscription/BillingInfo/View";
import { useActions, useSelector } from "app/utils/hooks";
import { fetchSelf } from "app/actions/user";
import {
  fetchPaymentInfo,
  fetchRecurlyPaymentInfo,
} from "app/actions/payment-info";
import { RootState } from "typedefs";
import RecurlyBillingInfo from "app/components/subscription/RecurlyBillingInfo";
import OrganizationBilling from "app/components/subscription/OrganizationBilling";
import { CustomerType } from "app/models/SubscriptionPlanModel";
import {
  canCancelLicensedPlan,
  isBilledByOrganization,
} from "app/utils/subscription";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";

const BillingInfo = () => {
  const { t } = useTranslation();
  const actions = useActions({
    fetchSelf,
    fetchPaymentInfo,
    fetchRecurlyPaymentInfo,
  });
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  // Derived on every render: self is null on the first one, and a snapshot
  // taken then would route everyone to the Recurly view for good. customerType
  // is optional, and "none" for a coach on a licensed seat billed outside his
  // own account.
  const billedByOrganization = isBilledByOrganization(self);
  const isStripeAccountType =
    self?.customerType?.toLowerCase() === CustomerType.STRIPE;

  useEffect(() => {
    // Somebody else holds the customer that pays for the seat, not the coach:
    // asking for his payment info answers 404, so don't ask.
    if (!self || billedByOrganization) {
      return;
    }

    if (isStripeAccountType) {
      actions.fetchSelf();
      actions.fetchPaymentInfo();
    } else {
      actions.fetchRecurlyPaymentInfo();
    }
    // Keyed on the id, not on self: fetchSelf replaces that object, and
    // depending on it would make this effect call itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [self?.id, billedByOrganization, isStripeAccountType]);

  if (!self) {
    return null;
  }

  const renderBillingInfo = () => {
    if (billedByOrganization) {
      return (
        <OrganizationBilling
          organizationName={self.plan?.organizationName}
          payerName={self.plan?.payerName}
          canCancelPool={canCancelLicensedPlan(self)}
        />
      );
    }
    return isStripeAccountType ? <BillingInfoView /> : <RecurlyBillingInfo />;
  };

  return (
    <AccountWrap>
      <PagePanel title={t("Billing")} titleStyles={styles.title}>
        {renderBillingInfo()}
      </PagePanel>
    </AccountWrap>
  );
};

export default BillingInfo;
